import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ParkerSubscriptionAssignmentDTO, ParkingSubscriptionDTO } from '../../../../../base/models/models.swagger.generated';
import { ClientService } from '../../../../../base/services/client/client.service';
import { GridBaseComponent } from '../../../../../base/shared/grid-base/grid-base.component';
import { SpinnerService } from '../../../../../base/spinner/spinner.service';
import { CommandClickEventArgs, CommandModel, GridModule } from '@syncfusion/ej2-angular-grids';
import { ModalHelperService } from '../../../../../base/shared/modal-helper';
import {
  EditParkerEditSubscriptionListFormComponent,
} from '../edit-parker-edit-subscription-list-form/edit-parker-edit-subscription-list-form.component';
import { DateTime } from 'luxon';
import { SubscriptionAssignmentFormType } from '../edit-parker-edit-subscription-list-form/edit-parker-edit-subscription-list-form.type';
import { SfCommandButtonType, SfRequestTypes } from 'src/app/base/models/syncfusion-data';
import { NgClass, DatePipe } from '@angular/common';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';

@Component({
    selector: 'app-edit-parker-subscription-list',
    templateUrl: './edit-parker-subscription-list.component.html',
    styleUrls: ['./edit-parker-subscription-list.component.scss'],
    imports: [
        GridModule,
        ToolbarModule,
        NgClass,
        EditParkerEditSubscriptionListFormComponent,
        DatePipe,
        TranslateModule,
    ]
})
export class EditParkerSubscriptionListComponent extends GridBaseComponent implements OnInit {

  @Input() public clientId: number;
  @Input() public subscriptions: ParkerSubscriptionAssignmentDTO[];
  @Output() public subscriptionsChange = new EventEmitter<ParkerSubscriptionAssignmentDTO[]>();
  @ViewChild(EditParkerEditSubscriptionListFormComponent) public editForm: EditParkerEditSubscriptionListFormComponent;

  public commands: CommandModel[] = [
    {
      type: SfCommandButtonType.Edit,
      buttonOption:
        {
          cssClass: 'icon-btn',
          iconCss: 'icon-edit e-icons',
        },
    },
    {
      type: SfCommandButtonType.Delete,
      buttonOption: {
        cssClass: 'icon-btn',
        iconCss: 'icon-trash-can e-icons e-danger',
      },
    }];

  public get useSetsAddMode(): boolean {
    return this._useSetsAddMode;
  }

  public formEnabled = false; // used to reset component
  private newItemIndexCount = -1;
  private _useSetsAddMode = false;

  constructor(clientService: ClientService,
              translate: TranslateService,
              public spinnerService: SpinnerService,
              private fb: FormBuilder,
              private modalHelperService: ModalHelperService) {
    super(clientService, translate, null);
  }

  public override onAdd(useSets: boolean = false): void {
    this._useSetsAddMode = useSets;
    super.onAdd();
  }

  public ngOnInit(): void {
    this.isActiveColumnName = 'active';
    this.editDialogTitle = 'i18n.domain.parker-administration.edit-subscription';
    this.addDialogTitle = 'i18n.domain.parker-administration.add-subscription';
    this.addIndexFindColumnName = 'name';

    this.editSettings = {...this.editSettings, showDeleteConfirmDialog: false};
  }

  public override actionBegin(args) {
    if (args.requestType === SfRequestTypes.BeginEdit || args.requestType === SfRequestTypes.Add) {
      this.formEnabled = true;
    }
    super.actionBegin(args);
  }

  public override actionComplete(args) {
    super.actionComplete(args);
    this.subscriptionsChange.emit(this.subscriptions);
    if (args.requestType === SfRequestTypes.Cancel || args.requestType === SfRequestTypes.Save) {
      this.formEnabled = false;
      this._useSetsAddMode = false;
    }
  }

  public commandClicked(args: CommandClickEventArgs): void {
    if (args.commandColumn.type === SfCommandButtonType.Delete) {
      args.cancel = true;
      this.modalHelperService.confirm(
        'i18n.core.modal.delete.title',
        'i18n.core.modal.delete.content',
        () => this.grid.deleteRecord('id', args.rowData),
      );
    }
  }

  protected initializeEditForm(data: ParkerSubscriptionAssignmentDTO): void {
    this.gridEditForm = this.fb.group<SubscriptionAssignmentFormType>({
      id: this.fb.control<number>(data.id || 0),
      subscriptionId: this.fb.control<number>(data.subscription?.id, [Validators.required]),
      active: this.fb.control<boolean>(data.active),
      isValidImmediately: this.fb.control<boolean>(data.validFrom == null),
      validFrom: this.fb.control<Date>(DateTime.isDateTime(data.validFrom) ? data.validFrom.toJSDate() : data.validFrom),
      isValidOpenEnd: this.fb.control<boolean>(data.validTo == null),
      validTo: this.fb.control<Date>(DateTime.isDateTime(data.validTo) ? data.validTo.toJSDate() : data.validTo),
      modifiedBy: this.fb.control<string>({value: data.modifiedBy, disabled: true}),
      modifiedOn: this.fb.control({value: data.modifiedOn ? data.modifiedOn.toFormat('dd.MM.yyyy HH:mm:ss') : "", disabled: true}),
      selectedSubscriptions: this.fb.control<ParkingSubscriptionDTO[]>(null)
    });
  }

  protected getEditDialogTitleParams(rowData: any) {
    return {params: ''};
  }

  protected override getDataToSave(): any {
    const data = this.gridEditForm.value;
    if (data.id === 0) {
      data.id = this.newItemIndexCount;
      this.newItemIndexCount = this.newItemIndexCount - 1;
    }
    const validFrom = DateTime.fromJSDate(data.validFrom ?? new Date());
    const validTo = data.validTo ? DateTime.fromJSDate(data.validTo) : null;

    let subscription: ParkingSubscriptionDTO;
    if (this._useSetsAddMode) {
      const set = this.editForm.allSubscriptionSets.find(set => set.id === this.gridEditForm.value.subscriptionId);
      subscription = this.editForm.allSubscriptionSets.find(set => set.id === this.gridEditForm.value.subscriptionId).subscriptions?.at(0);
      for (let i = 1; i < set.subscriptions.length; i++) {
        const id = this.newItemIndexCount;
        this.newItemIndexCount = this.newItemIndexCount - 1;
        this.subscriptions.push({
          ...data,
          id,
          validFrom,
          validTo,
          subscription: set.subscriptions.at(i),
        });
      }
    } else {
      subscription = this.editForm.allSubscriptions.find(sub => sub.id === this.gridEditForm.value.subscriptionId);
    }

    return {
      ...data,
      validFrom,
      validTo,
      subscription: subscription,
    };
  }
}
